/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { useConsoleServiceStore, useDxpFrameStore, useIdentityGatewayServiceStore } from 'dxpConsoleCore-mf/State';
import React, { useCallback, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { lastValueFrom } from 'rxjs/internal/lastValueFrom';
import { ERROR_SCREEN_TYPES } from '../../Screens/DxpErrorScreen/DxpErrorScreen.constants';
import {
  DxpTenantType,
  EntityDxpUserMe,
  EntityDxpUserMeBaseTenant,
  EntityDxpUserMeOrganisation,
  getConsoleService,
} from '../../Services/console';
import { checkScreenCondition } from '../../Utilities/CheckScreenCondition/CheckScreenCondition';
import { getFlatTenantList } from '../../Utilities/GetFlatTenantList/GetFlatTenantList';
import { isAgency } from '../../Utilities/IsAgency/IsAgency';
import { isOrganisation } from '../../Utilities/IsOrganisation/IsOrganisation';
import { setHeapEventProperties } from '../../Utilities/SetHeapEventProperties/SetHeapEventProperties';
import { SessionManager } from '../SessionManager/SessionManager';
import { DxpSecureRouteProps } from './DxpSecureRoute.props';

/**
 * DXP secure route generation.
 */
export const DxpSecureRoute = (props: DxpSecureRouteProps): JSX.Element => {
  const { children, item } = props;
  const { hasSession }: { hasSession: boolean } = useIdentityGatewayServiceStore();
  const { sessionIssue }: { sessionIssue: boolean } = useIdentityGatewayServiceStore();
  const { updateHasSession } = useIdentityGatewayServiceStore();
  const { updateNavigationSections } = useDxpFrameStore();
  const { updateTenant } = useConsoleServiceStore();
  const { consoleUser }: { consoleUser: EntityDxpUserMe } = useConsoleServiceStore();
  const { tenant }: { tenant: EntityDxpUserMeOrganisation } = useConsoleServiceStore();

  const { tenant: tenantParam } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect((): void => {
    let redirectPath = ``;
    let redirectSearch = ``;

    // No session could be created
    if (sessionIssue) {
      navigate({ pathname: `/error`, search: `?type=${ERROR_SCREEN_TYPES.unauthorizedError}` });
    }

    // No session available.
    if (!hasSession) {
      const consoleService = getConsoleService();

      // Check if a session was already established on a previous visit.
      lastValueFrom(consoleService.getHealth())
        .then(() => {
          updateHasSession(true);
          return true;
        })
        .catch(() => {
          const locationSearch = location && location.search;
          const authorizationCode = new URLSearchParams(locationSearch).get(`code`);
          const error = new URLSearchParams(locationSearch).get(`error`);

          if (authorizationCode) {
            const authorizationState = new URLSearchParams(locationSearch).get(`state`);
            redirectPath = `/authorize`;
            redirectSearch = `?code=${authorizationCode}&state=${authorizationState}`;
          } else if (error === ERROR_SCREEN_TYPES.unauthorizedError) {
            redirectPath = `/error`;
            redirectSearch = `?type=${error}`;
          } else {
            redirectPath = `/login`;
            redirectSearch = `?redirect_path=${window.location.pathname}`;
          }

          navigate({ pathname: redirectPath, search: redirectSearch });
          return true;
        });
    }

    // Session and console user are available
    if (hasSession && consoleUser) {
      // Check the tenant by :tenant parameter
      if (tenantParam) {
        // Get flat list of all tenants in the user's data
        const tenantList = consoleUser.tenantList;
        const allTenants = getFlatTenantList(tenantList) as Array<EntityDxpUserMeBaseTenant>;

        // Get the new tenant that the user wants to navigate to
        const newTenant = allTenants.find(
          (tenant) => tenant.tenantReferenceId.toLowerCase() === tenantParam.toLowerCase(),
        );

        // Redirect if they are using tenantId instead of tenantReferenceId
        if (!newTenant) {
          const matchByTenantId = allTenants.find((tenant) => tenant.tenantId === tenantParam);

          if (matchByTenantId) {
            const redirectPath = location.pathname.replace(matchByTenantId.tenantId, matchByTenantId.tenantReferenceId);
            navigate({
              pathname: redirectPath,
            });
            return;
          }
        }

        // Whether or not we should redirect to an error
        let showError = false;

        // Redirect the user if they do not belong to the new tenant.
        if (!newTenant) {
          showError = true;
        } else {
          // Else check the new tenant type (ie. organisation/agency) against the URL
          const newTenantType = newTenant.tenantType;

          // Redirect the user if URL contains '/agency', but new tenant is an org
          if (isAgency() && newTenantType === DxpTenantType.organisation) {
            showError = true;
          }

          // Redirect the user if URL contains '/organization', but new tenant is an agency
          else if (isOrganisation() && newTenantType === DxpTenantType.agency) {
            showError = true;
          }
        }

        // Handle error redirect
        if (showError) {
          navigate({
            pathname: `/error`,
            search: `?type=${ERROR_SCREEN_TYPES.noPageAccessError}`,
          });
          return;
        }

        // If we already have a tenant, check if we need to assign a new one
        let shouldUpdateTenant = false;

        if (tenant) {
          if (tenant.tenantReferenceId.toLowerCase() !== tenantParam.toLowerCase()) {
            // Update tenant because the tenant IDs don't match
            shouldUpdateTenant = true;
          }
        } else {
          // Update tenant because it doesn't exist
          shouldUpdateTenant = true;
        }

        if (shouldUpdateTenant) {
          // Set the new tenant and reset the menu
          updateTenant(newTenant);
          updateNavigationSections([]);
          setHeapEventProperties(newTenant);
        }
      } else {
        updateTenant(null);
        setHeapEventProperties();
      }
    }
  }, [
    consoleUser,
    hasSession,
    location,
    navigate,
    sessionIssue,
    tenant,
    tenantParam,
    updateHasSession,
    updateNavigationSections,
    updateTenant,
  ]);

  const checkItemAccess = useCallback((): boolean => {
    // If an item has been passed in, we should check for any display conditions.
    if (item) {
      const canAccessItem = checkScreenCondition(item);

      // If conditions are not met, redirect to the error screen.
      if (!canAccessItem) {
        navigate({
          pathname: `/error`,
          search: `?type=${ERROR_SCREEN_TYPES.unauthorizedError}`,
        });
        return false;
      }
    }

    return true;
  }, [item, navigate]);

  // Session is not yet determined.
  if (!hasSession) {
    return <></>;
  }

  // Session is all good and we have a console user
  if (hasSession && consoleUser) {
    // If we need a tenant for the page to function,
    // wait until we have one before rendering content.
    if (isOrganisation() || isAgency()) {
      if (tenant && checkItemAccess()) {
        return <SessionManager location={location}>{children}</SessionManager>;
      }
    } else {
      return <SessionManager location={location}>{children}</SessionManager>;
    }
  }

  // Session is likely missing
  return <></>;
};
